import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { slideService } from '../../services/slideService';

import { supabase } from '../../lib/supabase';
import SlideBuilder from './components/SlideBuilder';

// ─── Slide type definitions ───────────────────────────────────────────────────
const SLIDE_TYPES = [
{ value: 'builder', label: 'Slide Builder', icon: 'LayoutTemplate', description: 'Compose sections: text, images, Gantt, reminders' },
{ value: 'logo', label: 'Logo', icon: 'HardHat', description: 'Company logo with optional subtitle' },
{ value: 'image', label: 'Image', icon: 'Image', description: 'Full-screen image with optional caption' },
{ value: 'project_summary', label: 'Project Summary', icon: 'BarChart2', description: 'Live project counts and status overview' },
{ value: 'gantt', label: 'Gantt Chart', icon: 'GitBranch', description: 'Live Gantt chart for a selected project' },
{ value: 'resource_assignments', label: 'Resource Assignments', icon: 'Users', description: 'Current resource utilization and assignments' }];


const TYPE_COLORS = {
  builder: 'bg-amber-100 text-amber-700 border-amber-200',
  logo: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  image: 'bg-blue-100 text-blue-700 border-blue-200',
  project_summary: 'bg-purple-100 text-purple-700 border-purple-200',
  gantt: 'bg-green-100 text-green-700 border-green-200',
  resource_assignments: 'bg-indigo-100 text-indigo-700 border-indigo-200'
};

// ─── Slide Form Modal ─────────────────────────────────────────────────────────
const SlideFormModal = ({ slide, projects, onSave, onClose }) => {
  const isEdit = !!slide?.id;
  const [form, setForm] = useState({
    slideType: slide?.slideType || 'builder',
    title: slide?.title || '',
    durationSeconds: slide?.durationSeconds || 10,
    isActive: slide?.isActive !== undefined ? slide?.isActive : true,
    config: slide?.config || {}
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pasteHint, setPasteHint] = useState(false);
  const fileInputRef = useRef(null);
  const pasteZoneRef = useRef(null);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
  const setConfig = (key, val) => setForm((prev) => ({ ...prev, config: { ...prev?.config, [key]: val } }));

  const uploadImageFile = async (file) => {
    if (!file || !file?.type?.startsWith('image/')) return null;
    setUploading(true);
    setError(null);
    const result = await slideService?.uploadImage(file);
    setUploading(false);
    return result;
  };

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const result = await uploadImageFile(file);
    if (result?.success) {
      setConfig('imageUrl', result?.url);
    } else {
      setError(result?.error);
    }
  };

  const handlePaste = useCallback(async (e) => {
    if (form?.slideType !== 'image') return;
    const items = Array.from(e?.clipboardData?.items || []);
    const imageItem = items?.find(item => item?.type?.startsWith('image/'));
    if (!imageItem) return;
    e?.preventDefault();
    const file = imageItem?.getAsFile();
    if (!file) return;
    const result = await uploadImageFile(file);
    if (result?.success) {
      setConfig('imageUrl', result?.url);
    } else {
      setError(result?.error || 'Failed to paste image');
    }
  }, [form?.slideType]);

  useEffect(() => {
    if (form?.slideType !== 'image') return;
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [form?.slideType, handlePaste]);

  const handleSave = async () => {
    if (!form?.title?.trim()) {setError('Title is required');return;}
    setSaving(true);
    setError(null);
    const result = isEdit ?
    await slideService?.update(slide?.id, form) :
    await slideService?.create(form);
    setSaving(false);
    if (result?.error) {
      setError(result?.error?.message || 'Failed to save slide');
    } else {
      onSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Slide' : 'Add Slide'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error &&
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          }

          {/* Slide Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Slide Type</label>
            <div className="grid grid-cols-1 gap-2">
              {SLIDE_TYPES?.map((type) =>
              <button
                key={type?.value}
                onClick={() => {set('slideType', type?.value);set('config', {});}}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                form?.slideType === type?.value ?
                'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`
                }>

                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${TYPE_COLORS?.[type?.value]}`}>
                    <Icon name={type?.icon} size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                      {type?.label}
                      {type?.value === 'builder' &&
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">New</span>
                    }
                    </div>
                    <div className="text-xs text-gray-500">{type?.description}</div>
                  </div>
                  {form?.slideType === type?.value &&
                <Icon name="CheckCircle" size={18} color="#f59e0b" className="ml-auto flex-shrink-0" />
                }
                </button>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Slide Title</label>
            <input
              type="text"
              value={form?.title}
              onChange={(e) => set('title', e?.target?.value)}
              placeholder="e.g. Safety Reminder, Project Gantt, Site Photos..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />

          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Duration: <span className="text-amber-600">{form?.durationSeconds}s</span>
            </label>
            <input
              type="range"
              min={3}
              max={60}
              value={form?.durationSeconds}
              onChange={(e) => set('durationSeconds', parseInt(e?.target?.value))}
              className="w-full accent-amber-500" />

            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>3s</span><span>60s</span>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-700">Active</div>
              <div className="text-xs text-gray-500">Show this slide in the display</div>
            </div>
            <button
              onClick={() => set('isActive', !form?.isActive)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form?.isActive ? 'bg-amber-500' : 'bg-gray-300'}`}>

              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form?.isActive ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* ── BUILDER type: section composer ── */}
          {form?.slideType === 'builder' &&
          <div>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="LayoutTemplate" size={16} color="#f59e0b" />
                <label className="text-sm font-semibold text-gray-700">Slide Sections</label>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <SlideBuilder
                sections={form?.config?.sections || []}
                onChange={(sections) => setConfig('sections', sections)}
                backgroundColor={form?.config?.backgroundColor || '#0f172a'}
                onBackgroundColorChange={(color) => setConfig('backgroundColor', color)}
                projects={projects} />

              </div>
            </div>
          }

          {/* ── LOGO type ── */}
          {form?.slideType === 'logo' &&
          <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subtitle (optional)</label>
              <input
              type="text"
              value={form?.config?.subtitle || ''}
              onChange={(e) => setConfig('subtitle', e?.target?.value)}
              placeholder="e.g. Construction Management"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />

            </div>
          }

          {/* ── IMAGE type ── */}
          {form?.slideType === 'image' &&
          <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Image</label>
                {form?.config?.imageUrl &&
              <div className="mb-2 relative rounded-lg overflow-hidden h-32 bg-gray-100">
                    <img src={form?.config?.imageUrl} alt="Slide preview" className="w-full h-full object-cover" />
                    <button
                  onClick={() => setConfig('imageUrl', '')}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center">

                      <Icon name="X" size={12} />
                    </button>
                  </div>
              }
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

                {/* Paste zone */}
                <div
                  ref={pasteZoneRef}
                  tabIndex={0}
                  onFocus={() => setPasteHint(true)}
                  onBlur={() => setPasteHint(false)}
                  className={`mb-2 border-2 border-dashed rounded-lg p-3 text-center text-sm transition-colors cursor-pointer outline-none ${
                    pasteHint
                      ? 'border-violet-400 bg-violet-50 text-violet-600'
                      : 'border-violet-200 bg-violet-50/40 text-violet-500 hover:border-violet-400'
                  }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Clipboard" size={15} />
                    {uploading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-violet-500 border-t-transparent" />
                        Uploading from clipboard...
                      </span>
                    ) : (
                      <>Click here then press <kbd className="bg-white border border-violet-200 rounded px-1.5 py-0.5 font-mono text-xs">Ctrl+V</kbd> / <kbd className="bg-white border border-violet-200 rounded px-1.5 py-0.5 font-mono text-xs">⌘V</kbd> to paste image</>
                    )}
                  </span>
                </div>

                <button
                onClick={() => fileInputRef?.current?.click()}
                disabled={uploading}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500 hover:border-amber-400 hover:text-amber-600 transition-colors disabled:opacity-50">

                  {uploading ?
                <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent" />
                      Uploading...
                    </span> :

                <span className="flex items-center justify-center gap-2">
                      <Icon name="Upload" size={16} />
                      {form?.config?.imageUrl ? 'Replace Image' : 'Upload Image'}
                    </span>
                }
                </button>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Caption (optional)</label>
                <input
                type="text"
                value={form?.config?.caption || ''}
                onChange={(e) => setConfig('caption', e?.target?.value)}
                placeholder="Image caption..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />

              </div>
            </div>
          }

          {/* ── GANTT type ── */}
          {form?.slideType === 'gantt' &&
          <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Project</label>
                <select
                value={form?.config?.projectId || ''}
                onChange={(e) => setConfig('projectId', e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">

                  <option value="">— Use first active project —</option>
                  {projects?.map((p) =>
                <option key={p?.id} value={p?.id}>{p?.project_name}</option>
                )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">View Mode</label>
                <div className="flex gap-2">
                  {[{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]?.map((v) =>
                <button
                  key={v?.value}
                  onClick={() => setConfig('viewMode', v?.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  (form?.config?.viewMode || 'weekly') === v?.value ?
                  'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`
                  }>

                      {v?.label}
                    </button>
                )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Show Fields</label>
                <div className="flex flex-wrap gap-2">
                  {[{ value: 'phases', label: 'Phases' }, { value: 'deliverables', label: 'Deliverables' }, { value: 'tasks', label: 'Tasks' }, { value: 'subtasks', label: 'Subtasks' }]?.map((f) => {
                  const fields = form?.config?.fields || ['phases', 'deliverables'];
                  const active = fields?.includes(f?.value);
                  return (
                    <button
                      key={f?.value}
                      onClick={() => {
                        const next = active ? fields?.filter((x) => x !== f?.value) : [...fields, f?.value];
                        setConfig('fields', next?.length > 0 ? next : [f?.value]);
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      active ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`
                      }>

                        {f?.label}
                      </button>);

                })}
                </div>
              </div>
            </div>
          }
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-100 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white">

            {saving ?
            <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Saving...
              </span> :
            isEdit ? 'Save Changes' : 'Add Slide'}
          </Button>
        </div>
      </div>
    </div>);

};

// ─── IP Allowlist Panel ───────────────────────────────────────────────────────
const IP_STORAGE_KEY = 'slide_display_allowed_ips';

const IpAllowlistPanel = () => {
  const [ips, setIps] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(IP_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [newIp, setNewIp] = useState('');
  const [ipError, setIpError] = useState('');

  const isValidIp = (ip) => {
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6 = /^[0-9a-fA-F:]+$/;
    return ipv4?.test(ip) || ipv6?.test(ip);
  };

  const saveIps = (updated) => {
    setIps(updated);
    localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updated));
  };

  const handleAdd = () => {
    const trimmed = newIp?.trim();
    if (!trimmed) { setIpError('Enter an IP address'); return; }
    if (!isValidIp(trimmed)) { setIpError('Invalid IP address format'); return; }
    if (ips?.includes(trimmed)) { setIpError('IP already in list'); return; }
    saveIps([...ips, trimmed]);
    setNewIp('');
    setIpError('');
  };

  const handleRemove = (ip) => {
    saveIps(ips?.filter(i => i !== ip));
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleAdd();
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon name="Shield" size={20} color="#4f46e5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">IP Access Restriction</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Restrict the <span className="font-medium text-gray-700">/display</span> page to specific IP addresses.
            Leave empty to allow all IPs.
          </p>
        </div>
      </div>

      {/* Current IP list */}
      {ips?.length === 0 ? (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
          <Icon name="Globe" size={16} color="#16a34a" />
          <span className="text-sm text-green-700 font-medium">Open access — all IPs allowed</span>
        </div>
      ) : (
        <div className="mb-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Allowed IPs ({ips?.length})</p>
          {ips?.map((ip) => (
            <div key={ip} className="flex items-center justify-between px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={14} color="#4f46e5" />
                <span className="text-sm font-mono text-indigo-800">{ip}</span>
              </div>
              <button
                onClick={() => handleRemove(ip)}
                className="w-6 h-6 rounded bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center transition-colors"
                title="Remove IP"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new IP */}
      <div className="flex gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={newIp}
            onChange={(e) => { setNewIp(e?.target?.value); setIpError(''); }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 203.0.113.1"
            className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              ipError ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
          />
          {ipError && <p className="text-xs text-red-500 mt-1">{ipError}</p>}
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 flex-shrink-0"
        >
          <Icon name="Plus" size={15} color="#fff" />
          Add IP
        </button>
      </div>

      <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
        <Icon name="Info" size={12} color="#9ca3af" />
        Settings are saved locally in this browser and applied to the /display page on this device.
      </p>
    </div>
  );
};

// ─── Slide Card ───────────────────────────────────────────────────────────────
const SlideCard = ({ slide, index, total, onEdit, onDelete, onToggle, onMoveUp, onMoveDown, onDuplicate }) => {
  const typeInfo = SLIDE_TYPES?.find((t) => t?.value === slide?.slideType);
  const sectionCount = slide?.slideType === 'builder' ? slide?.config?.sections?.length || 0 : null;

  return (
    <div className={`bg-white rounded-xl border-2 transition-all ${slide?.isActive ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-60'}`}>
      <div className="p-4 flex items-center gap-4">
        {/* Order controls */}
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors">

            <Icon name="ChevronUp" size={16} />
          </button>
          <span className="text-xs font-bold text-gray-400 w-5 text-center">{index + 1}</span>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors">

            <Icon name="ChevronDown" size={16} />
          </button>
        </div>

        {/* Type icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${TYPE_COLORS?.[slide?.slideType]}`}>
          <Icon name={typeInfo?.icon || 'Monitor'} size={18} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{slide?.title || 'Untitled Slide'}</h3>
            {!slide?.isActive &&
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full flex-shrink-0">Hidden</span>
            }
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-500">{typeInfo?.label}</span>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-500">{slide?.durationSeconds}s</span>
            {sectionCount !== null &&
            <>
                <span className="text-xs text-gray-400">·</span>
                <span className="text-xs text-amber-600 font-medium">{sectionCount} section{sectionCount !== 1 ? 's' : ''}</span>
              </>
            }
            {slide?.slideType === 'gantt' && slide?.config?.projectId &&
            <>
                <span className="text-xs text-gray-400">·</span>
                <span className="text-xs text-gray-500 truncate">Project linked</span>
              </>
            }
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onToggle}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            slide?.isActive ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`
            }
            title={slide?.isActive ? 'Deactivate' : 'Activate'}>

            <Icon name={slide?.isActive ? 'Eye' : 'EyeOff'} size={14} />
          </button>
          <button
            onClick={onDuplicate}
            className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 flex items-center justify-center transition-colors"
            title="Duplicate">

            <Icon name="Copy" size={14} />
          </button>
          <button
            onClick={onEdit}
            className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors"
            title="Edit">

            <Icon name="Pencil" size={14} />
          </button>
          <button
            onClick={onDelete}
            className="w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition-colors"
            title="Delete">

            <Icon name="Trash2" size={14} />
          </button>
        </div>
      </div>
    </div>);

};

// ─── Main Page ────────────────────────────────────────────────────────────────
const SlideManagement = () => {
  const { userProfile } = useAuth();
  const [slides, setSlides] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [slidesRes, projectsRes] = await Promise.all([
        slideService?.getAll(),
        supabase?.from('projects')?.select('id, project_name, status')?.eq('is_archived', false)?.order('project_name', { ascending: true })
      ]);
      setSlides(slidesRes?.data || []);
      if (projectsRes?.error) {
        console.error('Error loading projects:', projectsRes?.error);
      }
      setProjects(projectsRes?.data || []);
    } catch (err) {
      console.error('Error loading slide data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSave = async () => {
    setModalOpen(false);
    setEditingSlide(null);
    await loadData();
    showToast('Slide saved successfully');
  };

  const handleDelete = async (id) => {
    setSaving(true);
    const { error } = await slideService?.delete(id);
    setSaving(false);
    setDeleteConfirm(null);
    if (error) {
      showToast('Failed to delete slide', 'error');
    } else {
      await loadData();
      showToast('Slide deleted');
    }
  };

  const handleToggle = async (slide) => {
    const { error } = await slideService?.update(slide?.id, { isActive: !slide?.isActive });
    if (!error) {
      setSlides((prev) => prev?.map((s) => s?.id === slide?.id ? { ...s, isActive: !s?.isActive } : s));
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newSlides = [...slides];
    [newSlides[index - 1], newSlides[index]] = [newSlides?.[index], newSlides?.[index - 1]];
    setSlides(newSlides);
    await slideService?.reorder(newSlides);
  };

  const handleMoveDown = async (index) => {
    if (index === slides?.length - 1) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[index + 1]] = [newSlides?.[index + 1], newSlides?.[index]];
    setSlides(newSlides);
    await slideService?.reorder(newSlides);
  };

  const handleDuplicate = async (slide) => {
    const result = await slideService?.duplicate(slide?.id, slides?.length);
    if (result?.error) {
      showToast('Failed to duplicate slide', 'error');
    } else {
      await loadData();
      showToast(`"${result?.data?.title}" created`);
    }
  };

  const activeCount = slides?.filter((s) => s?.isActive)?.length;

  return (
    <MainLayout>
      <div className="space-y-6 pb-8 mt-[26px]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Slide Management</h1>
            <p className="text-gray-500 text-sm mt-1">
              Configure the office display slideshow at{' '}
              <a
                href="/display"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2">

                /display
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/display"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">

              <Icon name="Monitor" size={16} />
              Preview Display
            </a>
            <a
              href="/display/control"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 border border-amber-300 bg-amber-50 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors">

              <Icon name="Smartphone" size={16} />
              Mobile Control
            </a>
            <Button
              onClick={() => {setEditingSlide(null);setModalOpen(true);}}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white flex items-center gap-2">

              <Icon name="Plus" size={16} color="#fff" />
              Add Slide
            </Button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
          { label: 'Total Slides', value: slides?.length, icon: 'Layers', color: 'text-gray-700' },
          { label: 'Active', value: activeCount, icon: 'Eye', color: 'text-green-600' },
          { label: 'Hidden', value: slides?.length - activeCount, icon: 'EyeOff', color: 'text-gray-400' },
          { label: 'Avg Duration', value: slides?.length > 0 ? `${Math.round(slides?.reduce((a, s) => a + (s?.durationSeconds || 10), 0) / slides?.length)}s` : '—', icon: 'Clock', color: 'text-amber-600' }]?.
          map((stat) =>
          <div key={stat?.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className={`${stat?.color}`}>
                <Icon name={stat?.icon} size={20} />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{stat?.value}</div>
                <div className="text-xs text-gray-500">{stat?.label}</div>
              </div>
            </div>
          )}
        </div>

        {/* Slides list */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Slide Deck</h2>
            <span className="text-xs text-gray-500">Use arrows to reorder</span>
          </div>

          {loading ?
          <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent" />
            </div> :
          slides?.length === 0 ?
          <div className="text-center py-16 text-gray-400">
              <Icon name="Layers" size={48} color="#d1d5db" />
              <p className="mt-3 font-medium text-gray-500">No slides yet</p>
              <p className="text-sm mt-1">Add your first slide to get started</p>
              <button
              onClick={() => {setEditingSlide(null);setModalOpen(true);}}
              className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors">

                Add First Slide
              </button>
            </div> :

          <div className="p-4 space-y-2">
              {slides?.map((slide, index) =>
            <SlideCard
              key={slide?.id}
              slide={slide}
              index={index}
              total={slides?.length}
              onEdit={() => {setEditingSlide(slide);setModalOpen(true);}}
              onDelete={() => setDeleteConfirm(slide?.id)}
              onToggle={() => handleToggle(slide)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              onDuplicate={() => handleDuplicate(slide)} />

            )}
            </div>
          }
        </div>

        {/* IP Allowlist Panel */}
        <IpAllowlistPanel />

        {/* Slide type legend */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Slide Types</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SLIDE_TYPES?.map((type) =>
            <div key={type?.value} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0 ${TYPE_COLORS?.[type?.value]}`}>
                  <Icon name={type?.icon} size={16} />
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-sm flex items-center gap-1">
                    {type?.label}
                    {type?.value === 'builder' &&
                  <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">New</span>
                  }
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{type?.description}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modalOpen &&
      <SlideFormModal
        slide={editingSlide}
        projects={projects}
        onSave={handleSave}
        onClose={() => {setModalOpen(false);setEditingSlide(null);}} />

      }

      {deleteConfirm &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={20} color="#ef4444" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Slide</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancel</Button>
              <button
              onClick={() => handleDelete(deleteConfirm)}
              disabled={saving}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-50">

                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      }

      {/* Toast */}
      {toast &&
      <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
      toast?.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`
      }>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} color="#fff" />
          {toast?.msg}
        </div>
      }
    </MainLayout>);

};

export default SlideManagement;